import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

/*
 * Pricing — single tier, dark-navy card.
 * Toggles: billing (monthly / annual) + currency (INR / USD / EUR).
 * "Learn more" → smooth-scroll to Product Tour (#product).
 * "Start trial" → same destination as every other primary CTA (/signup).
 */

const CURRENCIES = {
    INR: {
        symbol: "₹",
        monthly: "10,000",
        annual: "8,000",
        onboardingMonthly: "₹50,000 one-time",
        onboardingAnnual: "Waived on annual commitment",
    },
    USD: {
        symbol: "$",
        monthly: "119",
        annual: "95",
        onboardingMonthly: "$599 one-time",
        onboardingAnnual: "Waived on annual commitment",
    },
    EUR: {
        symbol: "€",
        monthly: "109",
        annual: "87",
        onboardingMonthly: "€551 one-time",
        onboardingAnnual: "Waived on annual commitment",
    },
};

const FEATURES = [
    "Tax-compliant invoicing with GST + e-invoice",
    "AR / AP / bank reconciliation with AI auto-match",
    "Multi-entity workspaces (up to 5 orgs)",
    "Up to 10 users · role-based access",
];

export default function Pricing() {
    const navigate = useNavigate();
    const [billing, setBilling] = useState("annual");
    const [currency, setCurrency] = useState("INR");
    const c = useMemo(() => CURRENCIES[currency], [currency]);
    const price = billing === "annual" ? c.annual : c.monthly;
    const onboarding = billing === "annual" ? c.onboardingAnnual : c.onboardingMonthly;

    return (
        <section className="vy-section" id="pricing" data-testid="landing-pricing">
            <div className="vy-container">
                <div className="vy-reveal" style={{ textAlign: "center", marginBottom: 32 }}>
                    <div className="vy-kicker">&gt; pricing --one-plan</div>
                    <h2 className="vy-serif vy-h-section" style={{ fontSize: "2.8rem", marginTop: 10, color: "var(--vy-navy)" }}>
                        One plan. <span className="vy-serif-i">Every entity.</span>
                    </h2>
                    <p style={{ marginTop: 12, color: "var(--vy-muted)", maxWidth: 560, margin: "12px auto 0", fontSize: 15 }}>
                        Simple, transparent pricing. Same plan whether you&apos;re one entity or five.
                    </p>

                    {/* Toggles */}
                    <div style={{ display: "flex", justifyContent: "center", gap: 12, flexWrap: "wrap", marginTop: 26 }}>
                        <div className="vy-tabs" role="tablist" aria-label="Billing frequency" data-testid="pricing-billing-toggle">
                            <button
                                type="button"
                                className={"vy-tab" + (billing === "monthly" ? " vy-tab-active" : "")}
                                onClick={() => setBilling("monthly")}
                                data-testid="billing-monthly"
                            >
                                monthly
                            </button>
                            <button
                                type="button"
                                className={"vy-tab" + (billing === "annual" ? " vy-tab-active" : "")}
                                onClick={() => setBilling("annual")}
                                data-testid="billing-annual"
                            >
                                annual · save 20%
                            </button>
                        </div>
                        <div className="vy-tabs" role="tablist" aria-label="Currency" data-testid="pricing-currency-toggle">
                            {Object.keys(CURRENCIES).map((cur) => (
                                <button
                                    key={cur}
                                    type="button"
                                    className={"vy-tab" + (currency === cur ? " vy-tab-active" : "")}
                                    onClick={() => setCurrency(cur)}
                                    data-testid={"currency-" + cur.toLowerCase()}
                                >
                                    {cur}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Single dark-navy card */}
                <div className="vy-reveal" style={{ display: "flex", justifyContent: "center" }}>
                    <div
                        className="vy-price-single"
                        data-testid="pricing-card"
                        style={{
                            background: "var(--vy-navy)",
                            color: "var(--vy-paper)",
                            border: "1px solid rgba(166,138,86,0.4)",
                            borderRadius: 2,
                            padding: 40,
                            maxWidth: 620,
                            width: "100%",
                            boxShadow: "0 24px 60px -30px rgba(19,25,46,0.35)",
                        }}
                    >
                        {/* Header: V logo + label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 14, paddingBottom: 22, borderBottom: "1px solid rgba(166,138,86,0.25)" }}>
                            <img
                                src="/vyaahar-logo.png"
                                alt=""
                                aria-hidden
                                style={{ height: 36, width: "auto", display: "block", flexShrink: 0 }}
                                data-testid="pricing-card-logo"
                            />
                            <div>
                                <div className="vy-mono" style={{ fontSize: 10, color: "var(--vy-gold)", letterSpacing: "0.16em", textTransform: "uppercase", marginTop: 3 }}>
                                    Finance &amp; Accounting Backbone
                                </div>
                            </div>
                        </div>

                        {/* Price */}
                        <div style={{ marginTop: 26 }}>
                            <div style={{ display: "flex", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
                                <span className="vy-serif" style={{ fontSize: 54, color: "var(--vy-gold)", letterSpacing: -1, lineHeight: 1 }} data-testid="price-value">
                                    {c.symbol}{price}
                                </span>
                                {billing === "annual" && (
                                    <span
                                        className="vy-mono"
                                        style={{ fontSize: 15, color: "rgba(246,242,235,0.45)", textDecoration: "line-through" }}
                                        data-testid="price-strikethrough"
                                    >
                                        {c.symbol}{c.monthly}
                                    </span>
                                )}
                                {billing === "annual" && (
                                    <span
                                        className="vy-mono"
                                        style={{
                                            fontSize: 10,
                                            padding: "4px 10px",
                                            background: "rgba(166,138,86,0.16)",
                                            color: "var(--vy-gold)",
                                            border: "1px solid rgba(166,138,86,0.4)",
                                            borderRadius: 2,
                                            letterSpacing: "0.12em",
                                            textTransform: "uppercase",
                                        }}
                                        data-testid="price-save-badge"
                                    >
                                        Save 20%
                                    </span>
                                )}
                            </div>
                            <div className="vy-mono" style={{ fontSize: 11.5, color: "rgba(246,242,235,0.65)", marginTop: 8 }}>
                                per workspace / month · {billing === "annual" ? "billed annually" : "billed monthly"}
                            </div>
                        </div>

                        {/* Onboarding row */}
                        <div
                            style={{
                                marginTop: 22,
                                padding: "12px 14px",
                                background: "rgba(246,242,235,0.04)",
                                border: "1px dashed rgba(166,138,86,0.3)",
                                borderRadius: 2,
                            }}
                            data-testid="onboarding-fee"
                        >
                            <div className="vy-mono" style={{ fontSize: 10, color: "var(--vy-gold)", letterSpacing: "0.14em", textTransform: "uppercase" }}>
                                Onboarding
                            </div>
                            <div style={{ fontSize: 13, color: "rgba(246,242,235,0.9)", marginTop: 4 }}>
                                {onboarding}
                            </div>
                        </div>

                        {/* Features */}
                        <ul style={{ listStyle: "none", padding: 0, margin: "26px 0 0", display: "grid", gap: 12 }} data-testid="pricing-features">
                            {FEATURES.map((f) => (
                                <li key={f} style={{ display: "flex", gap: 12, fontSize: 14, color: "rgba(246,242,235,0.92)", lineHeight: 1.4 }}>
                                    <Check size={16} strokeWidth={2} style={{ color: "var(--vy-gold)", flexShrink: 0, marginTop: 3 }} />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>

                        {/* CTAs */}
                        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 30 }}>
                            <a
                                href="#product"
                                className="vy-btn vy-btn-ghost-light"
                                style={{ flex: 1, justifyContent: "center", minWidth: 140 }}
                                data-testid="pricing-learn-more"
                            >
                                Learn more
                            </a>
                            <button
                                type="button"
                                className="vy-btn vy-btn-primary"
                                style={{ flex: 1, justifyContent: "center", minWidth: 140 }}
                                onClick={() => navigate("/signup")}
                                data-testid="pricing-start-trial"
                            >
                                Start trial
                                <span aria-hidden style={{ marginLeft: 4 }}>→</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
